import React, { useState } from "react";
import { Box, Flex, Grid, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickStartGuideExampleImage from "@/features/pathpick/features/start-guide/components/PathPickStartGuideExampleImage";
import PathPickStartGuideImageZoomModal from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideImageZoomModal";
import PathPickStartGuideMenuPath from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideMenuPath";
import PathPickStartGuideOfficialGuideLink from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideOfficialGuideLink";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import { SMARTSTORE_API_GUIDE_URL } from "@/features/pathpick/features/basket/constants";
import { START_GUIDE_IMAGES } from "@/features/pathpick/features/start-guide/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  // 이미 완료한 항목을 다시 열면 완료 버튼을 다시 누를 이유가 없다.
  isCompleted: boolean;
}

// 이미지 아래에서 확인시킬 항목 — 라벨/값 i18n 키를 짝지어 둔다.
const CHECKLIST = [
  { labelKey: "seller_id_label", valueKey: "seller_id_value" },
  // 판매자센터에서 라디오로 고르는 값이라, 선택된 라디오 모양을 값 앞에 같이 보여준다.
  {
    labelKey: "api_use_label",
    valueKey: "api_use_value",
    hasRadioMarker: true,
  },
  // 대행사 두 행은 "대행사 추가"를 눌러 우노트레이딩을 넣어야 한다는 점을 배지로 드러낸다.
  {
    labelKey: "product_agency_label",
    valueKey: "product_agency_value",
    hasAgencyBadge: true,
  },
  {
    labelKey: "order_agency_label",
    valueKey: "order_agency_value",
    hasAgencyBadge: true,
  },
] as const;

/**
 * 판매자센터에서 "사용" 라디오가 선택된 모습 — 조작용 컨트롤이 아니라 그림이다.
 *
 * Chakra Radio를 쓰지 않는 이유: 포커스/클릭이 가능해져 여기서 값을 바꿀 수 있는
 * 것처럼 오인된다. 옆의 "사용" 텍스트가 같은 정보를 읽어 주므로 aria-hidden.
 */
function ApiUseRadioMarker() {
  return (
    <Flex
      aria-hidden={true}
      width={"14px"}
      height={"14px"}
      borderRadius={"999px"}
      backgroundColor={"#4C994F"}
      alignItems={"center"}
      justifyContent={"center"}
      flexShrink={0}
    >
      <Box
        width={"6px"}
        height={"6px"}
        borderRadius={"999px"}
        backgroundColor={"#FFFFFF"}
      />
    </Flex>
  );
}

/**
 * 스마트스토어 API 설정 안내.
 *
 * API 연동용 판매자ID는 여기서 입력받지 않는다 — 다음 단계인 판매자 기본 정보 모달이
 * 그 값을 받는 유일한 자리다(전달서 §8.4). 여기서는 "메모해 두라"고만 알린다.
 */
function PathPickStartGuideApiSettingModal({
  isOpen,
  onClose,
  onComplete,
  isCompleted,
}: Props) {
  const { t } = useI18nHelper();
  const [isZoomOpen, setIsZoomOpen] = useState(false);

  const imageAlt = t("path_pick_guide:api_setting.image_alt");

  return (
    <>
      <CustomApprovalModal
        isOpen={isOpen}
        onClose={onClose}
        minWidth={"720px"}
        isCentered={true}
        scrollBehavior={"inside"}
        maxHeight={"92vh"}
      >
        <CustomApprovalModal.Header
          onClose={onClose}
          title={t("path_pick_guide:api_setting.title")}
        />
        <CustomApprovalModal.Body>
          <Flex flexDirection={"column"} gap={"12px"}>
            <PathPickStartGuideMenuPath
              path={t("path_pick_guide:api_setting.menu_path")}
            />
            <Text
              textStyle={"body-sm-medium"}
              color={"#6F6F6C"}
              whiteSpace={"pre-line"}
            >
              {t("path_pick_guide:api_setting.description")}
            </Text>

            <Flex
              flexDirection={"column"}
              gap={"8px"}
              padding={"12px"}
              borderRadius={"4px"}
              border={"0.5px solid #E5E5E3"}
            >
              <Flex
                justifyContent={"space-between"}
                alignItems={"center"}
                gap={"8px"}
              >
                <Text textStyle={"body-sm-bold"} color={"#22221F"}>
                  {t("path_pick_guide:api_setting.example_header")}
                </Text>
                {/* 크게 보기는 헤더 우측에만 둔다 — 이미지 아래에 같은 링크를 또 두지 않는다. */}
                <AppButton
                  variant={"normal"}
                  onClick={() => setIsZoomOpen(true)}
                >
                  {t("path_pick_guide:api_setting.zoom")}
                </AppButton>
              </Flex>
              <PathPickStartGuideExampleImage
                src={START_GUIDE_IMAGES.smartStoreApiSettings}
                alt={imageAlt}
              />
              <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                {t("path_pick_guide:api_setting.image_caption")}
              </Text>
            </Flex>

            <Grid
              gridTemplateColumns={"minmax(120px, 160px) 1fr"}
              borderTop={"0.5px solid #E5E5E3"}
            >
              {CHECKLIST.map((row) => {
                // 맞춰야 하는 목표 값인 행만 굵게 — 판매자ID 행은 안내 문장이라 그대로 둔다.
                const isTargetValue =
                  "hasAgencyBadge" in row || "hasRadioMarker" in row;

                return (
                  <React.Fragment key={row.labelKey}>
                    <Flex
                      padding={"8px 12px"}
                      backgroundColor={"#F7F7F5"}
                      borderBottom={"0.5px solid #E5E5E3"}
                      alignItems={"center"}
                    >
                      <Text textStyle={"body-sm-bold"} color={"#444440"}>
                        {t(
                          `path_pick_guide:api_setting.checklist.${row.labelKey}`
                        )}
                      </Text>
                    </Flex>
                    <Flex
                      padding={"8px 12px"}
                      borderBottom={"0.5px solid #E5E5E3"}
                      alignItems={"center"}
                    >
                      <Flex gap={"6px"} alignItems={"center"} flexWrap={"wrap"}>
                        {/* 셀러가 하는 동작 순서대로 읽히게 둔다 — 대행사 추가를 눌러 우노트레이딩을 넣는다. */}
                        {"hasAgencyBadge" in row && (
                          <>
                            <PathPickBadge
                              label={t(
                                "path_pick_guide:api_setting.agency_badge"
                              )}
                              colorVariant={"blue"}
                            />
                            <Text
                              textStyle={"caption-bold"}
                              color={"#8F8F8C"}
                              flexShrink={0}
                              fontSize={"16px"}
                            >
                              {"→"}
                            </Text>
                          </>
                        )}
                        {"hasRadioMarker" in row && <ApiUseRadioMarker />}
                        <Text
                          textStyle={
                            isTargetValue ? "body-sm-bold" : "body-sm-medium"
                          }
                          color={isTargetValue ? "#22221F" : "#444440"}
                        >
                          {t(
                            `path_pick_guide:api_setting.checklist.${row.valueKey}`
                          )}
                        </Text>
                      </Flex>
                    </Flex>
                  </React.Fragment>
                );
              })}
            </Grid>

            <PathPickInlineNotice tone={"blue"} leadingIcon={"info-circle"}>
              {t("path_pick_guide:api_setting.memo_notice")}
            </PathPickInlineNotice>

            <PathPickStartGuideOfficialGuideLink
              href={SMARTSTORE_API_GUIDE_URL}
            />

            <Flex gap={"8px"} justifyContent={"flex-end"} paddingTop={"4px"}>
              <AppButton
                variant={"primary"}
                isDisabled={isCompleted}
                onClick={onComplete}
              >
                {t("path_pick_guide:api_setting.done_cta")}
              </AppButton>
              <AppButton variant={"normal"} onClick={onClose}>
                {t("path_pick_guide:close")}
              </AppButton>
            </Flex>
          </Flex>
        </CustomApprovalModal.Body>
      </CustomApprovalModal>

      <PathPickStartGuideImageZoomModal
        isOpen={isZoomOpen}
        onClose={() => setIsZoomOpen(false)}
        title={t("path_pick_guide:api_setting.zoom_title")}
        src={START_GUIDE_IMAGES.smartStoreApiSettings}
        alt={imageAlt}
      />
    </>
  );
}

export default PathPickStartGuideApiSettingModal;
